import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";
import { initSentry } from "@/sentry";
import { registerBackgroundRejectionHandler } from "@/lib/errors/background-rejection";
import { isRecoverableHydrationError } from "@/lib/errors/hydration-recoverable";
import { getRootStore } from "@/stores/store-context";

const DEV_SERVICE_WORKER_RESET_KEY = "cognota:dev-service-worker-reset";

function clearDevelopmentBrowserCaches() {
  if (!import.meta.env.DEV || typeof window === "undefined") return;

  if ("caches" in window) {
    void window.caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => window.caches.delete(key))))
      .catch((err) => console.warn("[dev-cache] clear browser caches failed", err));
  }

  if (!("serviceWorker" in navigator)) return;

  const wasControlled = Boolean(navigator.serviceWorker.controller);

  void navigator.serviceWorker
    .getRegistrations()
    .then(async (registrations) => {
      if (registrations.length === 0) return false;
      await Promise.all(registrations.map((registration) => registration.unregister()));
      return wasControlled;
    })
    .then((shouldReload) => {
      if (!shouldReload || sessionStorage.getItem(DEV_SERVICE_WORKER_RESET_KEY)) return;
      sessionStorage.setItem(DEV_SERVICE_WORKER_RESET_KEY, "1");
      window.location.reload();
    })
    .catch((err) => console.warn("[dev-cache] service worker reset failed", err));
}

clearDevelopmentBrowserCaches();
initSentry();
registerBackgroundRejectionHandler();
// Feature flags start here, before hydration, not from an effect in the root.
// Route `clientLoader`s run during hydration and the root does not render until
// every one of them has resolved — so an effect in the root is by construction
// too late for a loader that needs a flag (the persisted table sort and filters,
// NG-517: `report-detail.tsx`, `projects.tsx`, the skills and custom-fields
// settings). With a root-effect initializer, a cold deep link or a hard reload
// found the store unready in every build: with LaunchDarkly disabled the flag
// read false from an override nobody had read yet, and with it enabled the
// loaders' bounded wait ran out on a client nobody had created. Either way the
// stored preference was neither restored nor, for that mount, persisted
// (NG-632). With LaunchDarkly disabled this is synchronous; with it enabled it
// only starts the client, and consumers wait on `isReady` as before.
getRootStore().featureFlags.initialize();

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>,
    {
      // SPA mode (ssr:false): the prerendered document is a static splash
      // shell, so a hydration mismatch/interruption is recoverable by
      // construction — React discards the shell and client-renders the same
      // spinner. It happens when something updates the router mid-hydration
      // on slow loads (e.g. URL normalization on a trailing-slash cold load),
      // which we can't fully prevent. Downgrade it to a warning so Sentry's
      // replay hydration detector stops filing prod issues (#3087); every
      // other recoverable error keeps React's default reporting.
      onRecoverableError(error, errorInfo) {
        if (isRecoverableHydrationError(error)) {
          console.warn("[hydration] recovered from shell hydration mismatch", {
            error,
            componentStack: errorInfo.componentStack,
          });
          return;
        }
        reportError(error);
      },
    },
  );
});
